import type {
  ApiResponse,
  CreateCategoryInput,
  CreateMenuItemInput,
  MenuCategory,
  MenuItem,
  UpdateCategoryInput,
  UpdateMenuItemInput,
  User,
  UserRole,
} from '@cracked-mug/shared';
import { api } from './api';

function unwrap<T>(payload: ApiResponse<T>): T {
  if (!payload.success) {
    throw new Error(payload.error);
  }
  return payload.data;
}

// ─── Menu items ──────────────────────────────────────────────────────────────

export async function adminCreateItem(input: CreateMenuItemInput): Promise<MenuItem> {
  const res = await api.post<ApiResponse<MenuItem>>('/admin/menu/items', input);
  return unwrap(res.data);
}

export async function adminUpdateItem(
  itemId: string,
  updates: UpdateMenuItemInput,
): Promise<MenuItem> {
  const res = await api.put<ApiResponse<MenuItem>>(`/admin/menu/items/${itemId}`, updates);
  return unwrap(res.data);
}

export async function adminDeleteItem(itemId: string): Promise<void> {
  await api.delete(`/admin/menu/items/${itemId}`);
}

// ─── Menu categories ─────────────────────────────────────────────────────────

export async function adminCreateCategory(input: CreateCategoryInput): Promise<MenuCategory> {
  const res = await api.post<ApiResponse<MenuCategory>>('/admin/menu/categories', input);
  return unwrap(res.data);
}

export async function adminUpdateCategory(
  categoryId: string,
  updates: UpdateCategoryInput,
): Promise<MenuCategory> {
  const res = await api.put<ApiResponse<MenuCategory>>(
    `/admin/menu/categories/${categoryId}`,
    updates,
  );
  return unwrap(res.data);
}

export async function adminDeleteCategory(categoryId: string): Promise<void> {
  await api.delete(`/admin/menu/categories/${categoryId}`);
}

// ─── Users ───────────────────────────────────────────────────────────────────

export async function adminListUsers(): Promise<User[]> {
  const res = await api.get<ApiResponse<User[]>>('/admin/users');
  return unwrap(res.data);
}

export async function adminSetUserRole(uid: string, role: UserRole): Promise<User> {
  const res = await api.patch<ApiResponse<User>>(`/admin/users/${uid}/role`, { role });
  return unwrap(res.data);
}

// ─── Analytics ───────────────────────────────────────────────────────────────

export interface AnalyticsPayload {
  windowDays: number;
  totalOrders: number;
  completedOrders: number;
  cancelledOrders: number;
  totalRevenueCents: number;
  averageOrderCents: number;
  topItems: { itemId: string; name: string; quantity: number }[];
  hourlyOrders: { hour: number; count: number }[];
}

export async function adminFetchAnalytics(windowDays = 30): Promise<AnalyticsPayload> {
  const res = await api.get<ApiResponse<AnalyticsPayload>>('/admin/analytics', {
    params: { windowDays },
  });
  return unwrap(res.data);
}
